/**
 * cozyhome-cart.js
 * Shared cart utility for Cozy Homes custom sections.
 *
 * After a successful /cart/add.js POST this helper:
 *   1. Refreshes the Horizon cart drawer contents via the Section Rendering API.
 *   2. Opens the Horizon cart drawer (theme-drawer#cart-drawer) by calling its
 *      native .open() method — the same path Horizon's own product-form.js uses.
 *   3. Falls back to window.location = '/cart' ONLY if the drawer element is
 *      genuinely absent (i.e. cart_type != 'drawer' or the DOM hasn't loaded it).
 *
 * Usage:
 *   await CozyCart.addAndOpen(variantId, quantity);
 *   await CozyCart.addAndOpen(variantId, quantity, { Colours: 'Red x2, Green x2' });
 *   // Returns { ok: true } on success, { ok: false, error } on failure.
 */
(function (global) {
  'use strict';

  /** Fetch the updated cart drawer HTML via Section Rendering API and swap it in. */
  async function refreshCartDrawer() {
    try {
      const cartItemsEl = document.querySelector('cart-items-component[data-drawer]');
      const sectionId = cartItemsEl ? cartItemsEl.dataset.sectionId : 'cart-drawer-section';
      const resp = await fetch(
        window.location.origin + '/?sections=' + sectionId,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const html = data[sectionId];
      if (!html) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newInner = doc.querySelector('.cart-drawer__inner');
      const curInner = document.querySelector('.cart-drawer__inner');
      if (newInner && curInner) {
        // Replace the WHOLE node, not just its innerHTML: the empty/full
        // state class (`cart-drawer--empty`) lives ON .cart-drawer__inner
        // itself. An innerHTML swap kept the stale empty-state class after
        // the FIRST item was added to an empty cart, leaving the drawer in a
        // broken half-empty render (badge + totals visible, line items not)
        // until a full page reload. replaceWith() brings the fresh class and
        // freshly-upgraded custom elements in one shot.
        curInner.replaceWith(newInner);
      }
    } catch (e) {
      // Silently ignore — cart opened, contents may be stale, drawer refresh is best-effort
    }
  }

  /**
   * Opens Horizon's native cart drawer.
   * Returns true if the drawer was found and opened; false otherwise (fallback needed).
   */
  function openCartDrawer() {
    const drawer = /** @type {any} */ (document.querySelector('#cart-drawer'));
    if (!drawer) return false;
    // ThemeDrawer custom element exposes .open() — same method Horizon's product-form uses
    if (typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  /**
   * POST to /cart/add.js, then open the cart drawer.
   * @param {number|string} variantId  - Shopify variant numeric id
   * @param {number}        quantity   - quantity to add (default 1)
   * @param {Object=}       properties - optional line item properties. Keys
   *        beginning with "_" are hidden from the cart/checkout UI but still
   *        recorded on the order. Used by the mix & match buy box to carry a
   *        set's per-item colour breakdown.
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  async function addAndOpen(variantId, quantity, properties) {
    quantity = quantity || 1;
    const item = { id: Number(variantId), quantity: Number(quantity) };
    // Only attach properties when there are some: an empty object still makes
    // Shopify treat the line as "has properties" and split it from an otherwise
    // identical plain line.
    if (properties && Object.keys(properties).length) item.properties = properties;
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      const data = await resp.json();
      if (!resp.ok || data.status) {
        return { ok: false, error: data.message || 'Add to cart failed' };
      }
      // Refresh cart contents, then open drawer (or fall back to /cart)
      await refreshCartDrawer();
      const opened = openCartDrawer();
      if (!opened) {
        window.location.href = '/cart';
      }
      return { ok: true };
    } catch (e) {
      window.location.href = '/cart';
      return { ok: false, error: String(e) };
    }
  }

  global.CozyCart = { addAndOpen: addAndOpen, openCartDrawer: openCartDrawer };
})(window);
